import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePositions } from '../utils/positions';
import {
  Box,
  TextField,
  Fab,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Chip,
  Slider,
  FormControlLabel,
  IconButton,
  Divider,
  Collapse,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Card,
  CardContent
} from '@mui/material'; 
import {
  FilterList,
  Close,
  ExpandMore,
  ExpandLess,
  Tune,
  School,
  Person,
  Refresh as RefreshIcon,
  Cancel as CancelIcon,
  RemoveRedEye as MonitorIcon,
  Star as StarIcon,
  HelpOutline as HelpOutlineIcon,
  Assignment as InformeIcon,
  Search as ScoutIcon,
  SportsBasketball as CompetitionIcon,
  RestartAlt
} from '@mui/icons-material';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import { useTheme, alpha } from '@mui/material/styles';

const getContrastColor = (hexcolor) => {
  if (!hexcolor) return '#000000';
  const r = parseInt(hexcolor.slice(1, 3), 16);
  const g = parseInt(hexcolor.slice(3, 5), 16);
  const b = parseInt(hexcolor.slice(5, 7), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#000000' : '#FFFFFF';
};

const PlayerFilters = ({
  isMobile,
  openFilterDrawer,
  setOpenFilterDrawer,
  nameFilter,
  setNameFilter,
  yearFilter,
  setYearFilter,
  positionFilter,
  setPositionFilter,
  groupFilter,
  setGroupFilter,
  heightFilter,
  setHeightFilter,
  ageFilter,
  setAgeFilter,
  playerTypeFilter,
  setPlayerTypeFilter,
  informeFilter,
  setInformeFilter,
  scoutFilter,
  setScoutFilter,
  groups,
  groupColors,
  verdictFilter,
  setVerdictFilter,
  competitionFilter,
  setCompetitionFilter,
  handleResetAllFilters,
  userRole,
}) => {
  const { t } = useTranslation();
  const { positionsWithKeys } = usePositions(t);
  const [expanded, setExpanded] = useState(true);
  const theme = useTheme();
  const primary = theme.palette.primary.main;

  const verdictOptions = [
    { value: 'DESCARTADO', label: t('playerFilters.verdictOptions.discarded'), emoji: '❌', color: '#f44336' },
    { value: 'VOLVER A VER', label: t('playerFilters.verdictOptions.watchAgain'), emoji: '🔄', color: '#2196f3' },
    { value: 'DUDAS', label: t('playerFilters.verdictOptions.dudas'), emoji: '❓', color: '#9c27b0' },
    { value: 'MONITORIZAR', label: t('playerFilters.verdictOptions.monitor'), emoji: '👁️', color: '#ff9800' },
    { value: 'PRIORITARIO', label: t('playerFilters.verdictOptions.priority'), emoji: '⭐', color: '#4caf50' }
  ];

  const handleInformeClick = (color) => {
    if (informeFilter === color) {
      setInformeFilter(null);
    } else {
      setInformeFilter(color);
    }
  };

  const handleScoutClick = (color) => {
    if (scoutFilter === color) {
      setScoutFilter(null);
    } else {
      setScoutFilter(color);
    }
  };

  const FilterCard = ({ label, filter, onColorClick, icon }) => (
    <Card 
      sx={{ 
        minWidth: 160, // Augmenté de 140 à 160
        height: 90, // Augmenté de 85 à 100
        border: '1px solid',
        borderColor: filter ? primary : '#e0e0e0',
        backgroundColor: filter ? alpha(primary, 0.06) : 'white',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          borderColor: primary,
          boxShadow: `0 2px 8px ${alpha(primary, 0.18)}`
        }
      }}
    >
      <CardContent sx={{ 
        pt: 0, // Suppression totale du padding top
        px: 1.5, 
        pb: 1.5,
        '&:last-child': { pb: 1.5 },
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center'
      }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {icon}
            <Typography 
              variant="body2" 
              sx={{ 
                fontWeight: 600,
                color: filter ? primary : '#666',
                fontSize: '1.1rem' // Augmenté encore plus pour être plus grand
              }}
            >
              {label}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.75 }}>
            {label === t('playerFilters.report') && (
              <Button
                variant={filter === 'zero' ? 'contained' : 'outlined'}
                size="small"
                onClick={() => onColorClick('zero')}
                sx={{
                  minWidth: 35, // Plus petit que les autres boutons
                  height: 38, // Même hauteur que les autres
                  fontSize: '0.85rem',
                  backgroundColor: filter === 'zero' ? '#9e9e9e' : 'transparent',
                  borderColor: '#9e9e9e',
                  color: filter === 'zero' ? 'white' : '#9e9e9e',
                  '&:hover': {
                    backgroundColor: filter === 'zero' ? '#757575' : 'rgba(158, 158, 158, 0.08)',
                    borderColor: '#9e9e9e',
                  }
                }}
              >
                0
              </Button>
            )}
            <Button
              variant={filter === 'red' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => onColorClick('red')}
              sx={{
                minWidth: 55, // Augmenté de 45 à 55
                height: 38, // Augmenté de 32 à 38
                fontSize: '0.85rem', // Augmenté de 0.75rem à 0.85rem
                backgroundColor: filter === 'red' ? '#f44336' : 'transparent',
                borderColor: '#f44336',
                color: filter === 'red' ? 'white' : '#f44336',
                '&:hover': {
                  backgroundColor: filter === 'red' ? '#d32f2f' : 'rgba(244, 67, 54, 0.08)',
                  borderColor: '#f44336',
                }
              }}
            >
              0-1
            </Button>
            <Button
              variant={filter === 'green' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => onColorClick('green')}
              sx={{
                minWidth: 55, // Augmenté de 45 à 55
                height: 38, // Augmenté de 32 à 38
                fontSize: '0.85rem', // Augmenté de 0.75rem à 0.85rem
                backgroundColor: filter === 'green' ? '#4caf50' : 'transparent',
                borderColor: '#4caf50',
                color: filter === 'green' ? 'white' : '#4caf50',
                '&:hover': {
                  backgroundColor: filter === 'green' ? '#388e3c' : 'rgba(76, 175, 80, 0.08)',
                  borderColor: '#4caf50',
                }
              }}
            >
              1+
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  const PlayerTypeCard = () => (
    <Card
      sx={{
        minWidth: 'fit-content',
        height: 90,
        border: '1px solid',
        borderColor: (playerTypeFilter === 1 || playerTypeFilter === 2) ? primary : '#e0e0e0',
        backgroundColor: (playerTypeFilter === 1 || playerTypeFilter === 2) ? alpha(primary, 0.06) : 'white',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          borderColor: primary,
          boxShadow: `0 2px 8px ${alpha(primary, 0.18)}`
        }
      }}
    >
      <CardContent sx={{
        pt: 0,
        px: 1.5,
        pb: 1.5,
        '&:last-child': { pb: 1.5 },
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center'
      }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: (playerTypeFilter === 1 || playerTypeFilter === 2) ? primary : '#666',
                fontSize: '1.1rem'
              }}
            >
              {t('playerFilters.playerType')}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 0.75 }}>
            <ToggleButton
              value="pro"
              selected={playerTypeFilter === 2}
              onChange={() => setPlayerTypeFilter(playerTypeFilter === 2 ? 0 : 2)}
              sx={{
                border: '1px solid',
                borderColor: playerTypeFilter === 2 ? '#ff5722' : '#e0e0e0',
                backgroundColor: playerTypeFilter === 2 ? 'rgba(255, 87, 34, 0.08)' : 'transparent',
                '&:hover': {
                  backgroundColor: playerTypeFilter === 2 ? 'rgba(255, 87, 34, 0.15)' : 'rgba(0, 0, 0, 0.04)',
                },
                '&.Mui-selected': {
                  backgroundColor: 'rgba(255, 87, 34, 0.08)',
                  borderColor: '#ff5722',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 87, 34, 0.15)',
                  }
                },
                px: 1.5,
                py: 0.75,
                minWidth: 70,
                height: 38,
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                <Person sx={{
                  fontSize: 16,
                  color: playerTypeFilter === 2 ? '#ff5722' : '#757575'
                }} />
                <Typography variant="caption" sx={{
                  color: playerTypeFilter === 2 ? '#ff5722' : '#757575',
                  fontWeight: playerTypeFilter === 2 ? 600 : 400,
                  fontSize: '0.7rem'
                }}>
                  {t('playerFilters.pro')}
                </Typography>
              </Box>
            </ToggleButton>

            <ToggleButton
              value="cantera"
              selected={playerTypeFilter === 1}
              onChange={() => setPlayerTypeFilter(playerTypeFilter === 1 ? 0 : 1)}
              sx={{
                border: '1px solid',
                borderColor: playerTypeFilter === 1 ? '#2196f3' : '#e0e0e0',
                backgroundColor: playerTypeFilter === 1 ? 'rgba(33, 150, 243, 0.08)' : 'transparent',
                '&:hover': {
                  backgroundColor: playerTypeFilter === 1 ? 'rgba(33, 150, 243, 0.15)' : 'rgba(0, 0, 0, 0.04)',
                },
                '&.Mui-selected': {
                  backgroundColor: 'rgba(33, 150, 243, 0.08)',
                  borderColor: '#2196f3',
                  '&:hover': {
                    backgroundColor: 'rgba(33, 150, 243, 0.15)',
                  }
                },
                px: 1.5,
                py: 0.75,
                minWidth: 'fit-content',
                height: 38,
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                <School sx={{
                  fontSize: 16,
                  color: playerTypeFilter === 1 ? '#2196f3' : '#757575'
                }} />
                <Typography variant="caption" sx={{
                  color: playerTypeFilter === 1 ? '#2196f3' : '#757575',
                  fontWeight: playerTypeFilter === 1 ? 600 : 400,
                  fontSize: '0.7rem',
                  whiteSpace: 'nowrap'
                }}>
                  {t('playerFilters.cantera')}
                </Typography>
              </Box>
            </ToggleButton>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  if (isMobile) {
    return (
      <Box>
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <TextField
            label={t('playerFilters.filterByNameOrTeam')}
            variant="outlined"
            value={nameFilter}
            onChange={(e) => setNameFilter(e.target.value)}
            sx={{ flex: 1 }}
          />
          <TextField
            label={t('playerFilters.filterByBirthYear')}
            variant="outlined"
            value={yearFilter}
            onChange={(e) => setYearFilter(e.target.value)}
            sx={{ flex: 1 }}
          />
        </Box>

        <Fab
          color="primary"
          sx={{ position: 'fixed', bottom: 16, right: 16 }}
          onClick={() => setOpenFilterDrawer(true)}
        >
          <FilterList />
        </Fab>

        <SwipeableDrawer
          anchor="bottom"
          open={openFilterDrawer}
          onClose={() => setOpenFilterDrawer(false)}
          onOpen={() => {}}
          sx={{
            '& .MuiDrawer-paper': {
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
              maxHeight: '80vh',
              pb: 2,
            },
          }}
        >
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">{t('playerFilters.filters')}</Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  startIcon={<RestartAlt />}
                  onClick={() => {
                    handleResetAllFilters && handleResetAllFilters();
                    setOpenFilterDrawer(false);
                  }}
                  sx={{
                    textTransform: 'none',
                    borderColor: 'error.main',
                    color: 'error.main',
                  }}
                >
                  {t('playerFilters.reset')}
                </Button>
                <IconButton onClick={() => setOpenFilterDrawer(false)}>
                  <Close />
                </IconButton>
              </Box>
            </Box>
            <Divider sx={{ mb: 2 }} />

            {userRole !== 'entrenador' && (
              <Box sx={{ mb: 3 }}>
                <PlayerTypeCard />
              </Box>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
              <FilterCard
                label={t('playerFilters.report')}
                filter={informeFilter}
                onColorClick={handleInformeClick}
                icon={<InformeIcon fontSize="small" />}
              />
              <FilterCard
                label={t('playerFilters.scout')}
                filter={scoutFilter}
                onColorClick={handleScoutClick}
                icon={<ScoutIcon fontSize="small" />}
              />
            </Box>

            {/* Filtre verdict pour mobile  */}
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>{t('playerFilters.verdicts')}</InputLabel>
              <Select
                multiple
                value={verdictFilter || []}
                onChange={(e) => setVerdictFilter(e.target.value)}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((verdict) => {
                      const option = verdictOptions.find(v => v.value === verdict);
                      return (
                        <Chip
                          key={verdict}
                          label={`${option?.label} ${option?.emoji}`}
                          size="small"
                          sx={{
                            backgroundColor: option?.color,
                            color: '#fff',
                          }}
                        />
                      );
                    })}
                  </Box>
                )}
              >
                {verdictOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <Checkbox checked={(verdictFilter || []).indexOf(option.value) > -1} />
                    <ListItemText primary={`${option.label} ${option.emoji}`} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>{t('playerFilters.position')}</InputLabel>
              <Select
                multiple
                value={positionFilter}
                onChange={(e) => setPositionFilter(e.target.value)}
                renderValue={(selected) => {
                  return selected.map(key => {
                    const pos = positionsWithKeys.find(p => p.key === key);
                    return pos ? pos.label : key;
                  }).join(', ');
                }}
              >
                {positionsWithKeys.map(({ key, label }) => (
                  <MenuItem key={key} value={key}>
                    <Checkbox checked={positionFilter.indexOf(key) > -1} />
                    <ListItemText primary={label} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>{t('playerFilters.groups')}</InputLabel>
              <Select
                multiple
                value={groupFilter}
                onChange={(e) => setGroupFilter(e.target.value)}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((group) => (
                      <Chip
                        key={group}
                        label={group === 'Sin Grupos' ? t('playerFilters.noGroups') : group}
                        sx={{
                          backgroundColor: groupColors[group] || 'grey',
                          color: getContrastColor(groupColors[group]),
                        }}
                      />
                    ))}
                  </Box>
                )}
              >
                {groups.map((group) => (
                  <MenuItem key={group.libelle} value={group.libelle}>
                    <Checkbox checked={groupFilter.indexOf(group.libelle) > -1} />
                    <ListItemText primary={group.libelle} />
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        backgroundColor: group.code_couleur || 'grey',
                        borderRadius: '50%',
                        ml: 1,
                      }}
                    />
                  </MenuItem>
                ))}
                <MenuItem value="Sin Grupos">
                  <Checkbox checked={groupFilter.indexOf('Sin Grupos') > -1} />
                  <ListItemText primary={t('playerFilters.noGroups')} />
                </MenuItem>
              </Select>
            </FormControl>

            {/* Filtre Competición pour mobile */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>{t('playerFilters.competition')}</Typography>
              <ToggleButtonGroup
                value={competitionFilter}
                exclusive
                onChange={(e, newValue) => setCompetitionFilter(newValue)}
                sx={{ width: '100%' }}
              >
                <ToggleButton value="with" sx={{ flex: 1 }}>
                  {t('playerFilters.withCompetition')}
                </ToggleButton>
                <ToggleButton value="without" sx={{ flex: 1 }}>
                  {t('playerFilters.withoutCompetition')}
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <Typography gutterBottom>{t('playerFilters.heightRange')}</Typography>
            <Slider
              value={heightFilter}
              onChange={(e, newValue) => setHeightFilter(newValue)}
              valueLabelDisplay="auto"
              min={100}
              max={230}
              sx={{ mb: 3 }}
            />

            <Typography gutterBottom>{t('playerFilters.ageRange')}</Typography>
            <Slider
              value={ageFilter}
              onChange={(e, newValue) => setAgeFilter(newValue)}
              valueLabelDisplay="auto"
              min={8}
              max={42}
            />
          </Box>
        </SwipeableDrawer>
      </Box>
    );
  }

  return (
    <Box>
      <Collapse in={expanded}>
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
            {userRole !== 'entrenador' && <PlayerTypeCard />}

            <FilterCard
              label={t('playerFilters.report')}
              filter={informeFilter}
              onColorClick={handleInformeClick}
              icon={<InformeIcon fontSize="small" />}
            />

            <FilterCard
              label={t('playerFilters.scout')}
              filter={scoutFilter}
              onColorClick={handleScoutClick}
              icon={<ScoutIcon fontSize="small" />}
            />
            
            {/* Altura + Edad : sliders empilés et bornés, alignés sur la hauteur des cartes */}
            <Box
              sx={{
                flex: '1 1 320px',
                minWidth: 260,
                maxWidth: 480,
                height: 90,
                px: 2,
                border: '1px solid #e0e0e0',
                borderRadius: '12px',
                backgroundColor: 'white',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                gap: 0.25,
              }}
            >
              <Box>
                <Typography variant="caption" sx={{ fontSize: '0.75rem', color: '#666' }}>
                  {t('playerFilters.height')} (cm)
                </Typography>
                <Slider
                  size="small"
                  value={heightFilter}
                  onChange={(e, newValue) => setHeightFilter(newValue)}
                  valueLabelDisplay="auto"
                  min={100}
                  max={230}
                  sx={{ width: '100%', py: 0.25 }}
                />
              </Box>
              <Box>
                <Typography variant="caption" sx={{ fontSize: '0.75rem', color: '#666' }}>
                  {t('playerFilters.age')}
                </Typography>
                <Slider
                  size="small"
                  value={ageFilter}
                  onChange={(e, newValue) => setAgeFilter(newValue)}
                  valueLabelDisplay="auto"
                  min={8}
                  max={42}
                  sx={{ width: '100%', py: 0.25 }}
                />
              </Box>
            </Box>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3, alignItems: 'center' }}>
          <TextField
            label={t('playerFilters.filterByNameOrTeam')}
            variant="outlined"
            value={nameFilter}
            onChange={(e) => setNameFilter(e.target.value)}
            size="small"
            sx={{ flex: '1 1 200px', minWidth: 180 }}
          />
          <TextField
            label={t('playerFilters.filterByBirthYear')}
            variant="outlined"
            value={yearFilter}
            onChange={(e) => setYearFilter(e.target.value)}
            size="small"
            sx={{ flex: '1 1 150px', minWidth: 140 }}
          />
          <FormControl size="small" sx={{ flex: '1 1 180px', minWidth: 160 }}>
            <InputLabel>{t('playerFilters.position')}</InputLabel>
            <Select
              multiple
              value={positionFilter}
              onChange={(e) => setPositionFilter(e.target.value)}
              renderValue={(selected) => {
                return selected.map(key => {
                  const pos = positionsWithKeys.find(p => p.key === key);
                  return pos ? pos.label : key;
                }).join(', ');
              }}
            >
              {positionsWithKeys.map(({ key, label }) => (
                <MenuItem key={key} value={key}>
                  <Checkbox checked={positionFilter.indexOf(key) > -1} />
                  <ListItemText primary={label} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ flex: '1 1 180px', minWidth: 160 }}>
            <InputLabel>{t('playerFilters.groups')}</InputLabel>
            <Select
              multiple
              value={groupFilter}
              onChange={(e) => setGroupFilter(e.target.value)}
              renderValue={(selected) => (
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 0.5,
                    maxWidth: '100%',
                    overflow: 'hidden',
                  }}
                >
                  {selected.length <= 2 ? (
                    selected.map((group) => (
                      <Chip
                        key={group}
                        label={group === 'Sin Grupos' ? t('playerFilters.noGroups') : group}
                        size="small"
                        sx={{
                          backgroundColor: groupColors[group] || 'grey',
                          color: getContrastColor(groupColors[group]),
                          maxWidth: '120px',
                          '& .MuiChip-label': {
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          },
                        }}
                      />
                    ))
                  ) : (
                    <Typography variant="body2">{t('playerFilters.groupsCount', { count: selected.length })}</Typography>
                  )}
                </Box>
              )}
            >
              {groups.map((group) => (
                <MenuItem key={group.libelle} value={group.libelle}>
                  <Checkbox checked={groupFilter.indexOf(group.libelle) > -1} />
                  <ListItemText primary={group.libelle} />
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      backgroundColor: group.code_couleur || 'grey',
                      borderRadius: '50%',
                      ml: 1,
                    }}
                  />
                </MenuItem>
              ))}
              <MenuItem value="Sin Grupos">
                <Checkbox checked={groupFilter.indexOf('Sin Grupos') > -1} />
                <ListItemText primary={t('playerFilters.noGroups')} />
              </MenuItem>
            </Select>
          </FormControl>

          {/* Verdict (déplacé depuis la ligne 1) */}
          <FormControl size="small" sx={{ flex: '1 1 170px', minWidth: 150 }}>
            <InputLabel>{t('playerFilters.verdict')}</InputLabel>
            <Select
              multiple
              value={verdictFilter || []}
              onChange={(e) => setVerdictFilter(e.target.value)}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  {selected.length > 2 ? (
                    <Typography variant="body2">{selected.length} {t('playerFilters.selected')}</Typography>
                  ) : (
                    selected.map((verdict) => {
                      const option = verdictOptions.find(v => v.value === verdict);
                      return (
                        <Box key={verdict} sx={{ fontSize: '16px' }}>
                          {option?.emoji}
                        </Box>
                      );
                    })
                  )}
                </Box>
              )}
            >
              {verdictOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Checkbox checked={(verdictFilter || []).indexOf(option.value) > -1} />
                  <ListItemText primary={option.label} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* NOUVEAU : Filtre Competición */}
          <ToggleButtonGroup
            value={competitionFilter}
            exclusive
            onChange={(e, newValue) => setCompetitionFilter(newValue)}
            size="small"
            sx={{ 
              height: '40px',
              '& .MuiToggleButton-root': {
                px: 2,
                fontSize: '0.875rem',
                '&.Mui-selected': {
                  backgroundColor: primary,
                  color: 'white',
                  '&:hover': {
                    backgroundColor: theme.palette.primary.dark,
                  }
                }
              }
            }}
          >
            <ToggleButton value="with">
              <CompetitionIcon sx={{ mr: 0.5, fontSize: 18 }} />
              {t('playerFilters.withCompShort')}
            </ToggleButton>
            <ToggleButton value="without">
              {t('playerFilters.withoutCompShort')}
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Collapse>
    </Box>
  );
};

export default PlayerFilters;